import { useState, useCallback, useEffect } from 'react';
import { useToast } from '@cargomatic/ui';
import { useMutation } from '@tanstack/react-query';
import { useShipmentById } from './useShipmentById';
import ShipmentStore from 'stores/ShipmentStore';
import SessionStore from 'stores/SessionStore';
import { addOrderBaseCost } from 'utils/OrderWebAPIUtils';
import { addAdjustment } from 'actions/ShipmentsActionCreators';
export function useOrderRtbActions({ orderId, arLines, apLines, refetchArLines, refetchApLines, currency = 'USD', }) {
    var _a;
    const { toast } = useToast();
    // Single active action state
    const [activeAction, setActiveAction] = useState(null);
    // Base cost specific state (needs input value)
    const [baseCostValue, setBaseCostValue] = useState('');
    // Pending refetch tracking
    const [pendingRefetch, setPendingRefetch] = useState(null);
    // Single shipment fetch based on active action
    const shipmentId = activeAction && 'charge' in activeAction
        ? (_a = activeAction.charge.shipment) === null || _a === void 0 ? void 0 : _a._id
        : undefined;
    const { data: shipment, isLoading: isLoadingShipment } = useShipmentById(shipmentId, {
        enabled: Boolean(activeAction),
        staleTime: 5000, // 5 seconds - shorter stale time
    });
    // Listen to ShipmentStore for successful updates and trigger refetch
    useEffect(() => {
        if (!pendingRefetch)
            return;
        const handleStoreChange = () => {
            if (pendingRefetch === 'receivables') {
                refetchArLines();
            }
            else {
                refetchApLines();
            }
            setPendingRefetch(null);
        };
        ShipmentStore.addChangeListener(handleStoreChange);
        return () => {
            ShipmentStore.removeChangeListener(handleStoreChange);
        };
    }, [pendingRefetch, refetchArLines, refetchApLines]);
    // Helper to convert tab type to ledger type
    const toLedgerType = (tab) => tab === 'ar' ? 'receivables' : 'payables';
    // Clear active action
    const clearAction = useCallback(() => {
        setActiveAction(null);
        setBaseCostValue('');
    }, []);
    // Action triggers
    const editCharge = useCallback((charge, tab) => {
        var _a;
        if (!((_a = charge.shipment) === null || _a === void 0 ? void 0 : _a._id)) {
            toast({
                title: 'Error',
                description: 'No shipment associated with this charge.',
                variant: 'destructive',
            });
            return;
        }
        setActiveAction({ type: 'edit', charge, ledger: toLedgerType(tab) });
    }, [toast]);
    const addInternalAdjustment = useCallback((charge, tab) => {
        var _a;
        if (!((_a = charge.shipment) === null || _a === void 0 ? void 0 : _a._id)) {
            toast({
                title: 'Error',
                description: 'No shipment associated with this charge.',
                variant: 'destructive',
            });
            return;
        }
        setActiveAction({
            type: 'internal-adjustment',
            charge,
            ledger: toLedgerType(tab),
        });
    }, [toast]);
    const addCustomerAdjustment = useCallback((charge, tab) => {
        var _a;
        if (!((_a = charge.shipment) === null || _a === void 0 ? void 0 : _a._id)) {
            toast({
                title: 'Error',
                description: 'No shipment associated with this charge.',
                variant: 'destructive',
            });
            return;
        }
        setActiveAction({
            type: 'customer-adjustment',
            charge,
            ledger: toLedgerType(tab),
        });
    }, [toast]);
    const addBaseCost = useCallback((tab) => {
        setBaseCostValue('');
        setActiveAction({ type: 'add-base-cost', ledger: toLedgerType(tab) });
    }, []);
    // Save handlers
    const saveAdjustment = useCallback((adjustment) => {
        if (!shipment || !activeAction)
            return;
        addAdjustment(shipment, activeAction.ledger, adjustment);
        setPendingRefetch(activeAction.ledger);
        clearAction();
    }, [shipment, activeAction, clearAction]);
    const baseCostMutation = useMutation({
        mutationFn: (params) => addOrderBaseCost(orderId, params.ledgerType, params.baseCost),
        onSuccess: (_data, variables) => {
            toast({
                title: 'Base Cost Added',
                description: 'Base cost has been added successfully.',
            });
            if (variables.ledgerType === 'receivables') {
                refetchArLines();
            }
            else {
                refetchApLines();
            }
            clearAction();
        },
        onError: () => {
            toast({
                title: 'Error',
                description: 'Failed to add base cost. Please try again.',
                variant: 'destructive',
            });
        },
    });
    const saveBaseCost = useCallback(() => {
        if (!activeAction || activeAction.type !== 'add-base-cost')
            return;
        if (!orderId || !baseCostValue)
            return;
        const numValue = parseFloat(baseCostValue);
        if (isNaN(numValue) || numValue <= 0) {
            toast({
                title: 'Invalid Value',
                description: 'Please enter a valid positive number.',
                variant: 'destructive',
            });
            return;
        }
        const baseCost = {
            value: numValue,
            type: activeAction.ledger === 'receivables' ? 'shipper_cost' : 'carrier_cost',
        };
        baseCostMutation.mutate({ ledgerType: activeAction.ledger, baseCost });
    }, [activeAction, orderId, baseCostValue, toast, baseCostMutation]);
    // Utility: Check if user can add adjustments
    const canAddAdjustment = useCallback((charge) => {
        const isFinanceUser = SessionStore.hasFinanceRole();
        const shipmentIsCurrent = true; // Simplified for order-level view
        return ((isFinanceUser || shipmentIsCurrent) &&
            charge.category !== 'rate_adjustments' &&
            charge.category !== 'incentive_payments');
    }, []);
    // Utility: Check if order needs base cost
    const orderNeedsBaseCost = useCallback((tab) => {
        const allCharges = tab === 'ar' ? arLines : apLines;
        return !allCharges.some((c) => c.category === 'base_cost');
    }, [arLines, apLines]);
    // Derived state for modals
    const isEditModalOpen = (activeAction === null || activeAction === void 0 ? void 0 : activeAction.type) === 'edit';
    const isInternalAdjustmentModalOpen = (activeAction === null || activeAction === void 0 ? void 0 : activeAction.type) === 'internal-adjustment';
    const isCustomerAdjustmentModalOpen = (activeAction === null || activeAction === void 0 ? void 0 : activeAction.type) === 'customer-adjustment';
    const isAdjustmentModalOpen = isInternalAdjustmentModalOpen || isCustomerAdjustmentModalOpen;
    const adjustmentType = (activeAction === null || activeAction === void 0 ? void 0 : activeAction.type) === 'customer-adjustment' ? 'customer' : 'internal';
    const isBaseCostDialogOpen = (activeAction === null || activeAction === void 0 ? void 0 : activeAction.type) === 'add-base-cost';
    return {
        // Active action state
        activeAction,
        shipment,
        isLoadingShipment,
        // Action triggers
        editCharge,
        addInternalAdjustment,
        addCustomerAdjustment,
        addBaseCost,
        clearAction,
        // Save handlers
        saveAdjustment,
        saveBaseCost,
        // Base cost specific
        baseCostValue,
        setBaseCostValue,
        isSavingBaseCost: baseCostMutation.isPending,
        // Utilities
        canAddAdjustment,
        orderNeedsBaseCost,
        // Modal states (derived)
        isEditModalOpen,
        isAdjustmentModalOpen,
        adjustmentType,
        isBaseCostDialogOpen,
        // For pending refetch (if needed externally)
        setPendingRefetch,
        // Currency pass-through
        currency,
    };
}
